/**
 * Social sign-in configuration.
 *
 * A Google OAuth client id is **per deployment**, not per product: it is bound to the
 * origins and the Android signing certificate it is issued for. Committing one would
 * tie every build of this app to whichever project happened to be set up first, and
 * the second deployment would fail with an error that names nothing useful
 * (`idpiframe_initialization_failed`). So it is empty here and set where the build is
 * configured.
 *
 * Everything downstream is written and inert: `oauthExchange()` in
 * `entities/session/api.ts`, the outcome handling in the auth store, the button on
 * the sign-in screen. Filling this in is the whole activation — the button appears,
 * because `oauthEnabled()` is what hides it.
 *
 * ## Getting one
 *
 * Google Cloud console → APIs & Services → Credentials → OAuth client ID:
 *
 *   - **Web application** for the browser build. Add the cabinet's origins to
 *     "Authorized JavaScript origins" (`https://cabinet.sam-vision.uz`, and
 *     `http://localhost:8099` for `npm run web`). This is the id below.
 *   - **Android** for the APK, with the package name (`com.samvision_cabinet`) and the
 *     SHA-1 of the signing certificate — a separate id, needed only once the native
 *     seam in `shared/lib/google-signin.ts` is wired to a sign-in library. The WEB id
 *     is still what that library sends as `webClientId`, because the credential the
 *     gateway verifies has to have the web client as its audience.
 *
 * The client id is not a secret — it ships inside every page and every APK by design.
 * The client *secret* is, and nothing here needs one: this is the implicit/ID-token
 * flow, where the gateway verifies Google's signature on a token the browser obtained
 * directly.
 */

/** Empty ⇒ social sign-in is not offered. See above. */
export const GOOGLE_CLIENT_ID = '';

/** The `provider` value `POST /auth/oauth/exchange` expects. */
export const GOOGLE_PROVIDER = 'google';

export function oauthEnabled(): boolean {
  return GOOGLE_CLIENT_ID.length > 0;
}
